/**
 * CECL Admin Config — Phase 16 Track B.
 *
 * Admin page for the USE_CECL_FOR_CONTRACT_ASSETS enable toggle.
 * PATCH /api/v1/revenue/org-config/cecl-enable with { enabled: bool }.
 * Enable returns 409 CECL_BACKFILL_INCOMPLETE when unassigned contracts exist.
 */
import { useState, useEffect, useCallback } from 'react';
import { API_V1, authFetch } from '../constants';
import { useGlobalEntity, withEntityParam } from '../hooks/useGlobalEntity';
import { useNavigate } from 'react-router-dom';
import PageLayout from '../components/ui/PageLayout';
import ActionButton from '../components/ui/ActionButton';
import Modal from '../components/ui/Modal';

const REV_API = `${API_V1}/revenue`;

interface BackfillBlock {
  unassigned_contract_ids?: number[];
  unassigned_count?: number;
  message?: string;
}

export default function CECLAdminConfig() {
  const { entity } = useGlobalEntity();
  const navigate = useNavigate();
  const [enabled, setEnabled] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  const [backfillBlock, setBackfillBlock] = useState<BackfillBlock | null>(null);

  // Disable confirmation modal
  const [disableConfirmOpen, setDisableConfirmOpen] = useState(false);

  const loadState = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await authFetch(
        withEntityParam(`${API_V1}/org-config/USE_CECL_FOR_CONTRACT_ASSETS`, entity),
      );
      if (r.ok) {
        const d = await r.json();
        const raw = d?.value ?? d?.enabled ?? d?.config_value;
        if (typeof raw === 'boolean') setEnabled(raw);
        else if (typeof raw === 'string') setEnabled(raw.toLowerCase() === 'true');
        else setEnabled(null);
      } else if (r.status === 404) {
        // Org config entry not yet created — default off
        setEnabled(false);
      } else {
        const err = await r.json().catch(() => ({}));
        setError(err.detail || `HTTP ${r.status}`);
      }
    } catch (e: any) {
      setError(e?.message || 'Network error');
    }
    setLoading(false);
  }, [entity]);

  useEffect(() => { loadState(); }, [loadState]);

  const flip = async (target: boolean) => {
    setSubmitting(true);
    setError(null);
    setSuccessMsg(null);
    setBackfillBlock(null);
    try {
      const r = await authFetch(withEntityParam(`${REV_API}/org-config/cecl-enable`, entity), {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled: target }),
      });
      if (r.ok) {
        setSuccessMsg(target
          ? 'CECL engine enabled. New contract assets will accrue allowances automatically.'
          : 'CECL engine disabled. Existing allowances are preserved; no recompute until re-enabled.');
        await loadState();
      } else if (r.status === 409) {
        const err = await r.json().catch(() => ({}));
        const code = err?.error_code || err?.code;
        const detail = err?.detail || err?.message;
        if (code === 'CECL_BACKFILL_INCOMPLETE' || (typeof detail === 'string' && detail.includes('BACKFILL_INCOMPLETE'))) {
          setBackfillBlock({
            unassigned_contract_ids: err?.unassigned_contract_ids || [],
            unassigned_count: err?.unassigned_count || (err?.unassigned_contract_ids?.length ?? 0),
            message: typeof detail === 'string' ? detail : 'Unassigned contracts with contract-asset exposure exist.',
          });
        } else {
          setError(typeof detail === 'string' ? detail : `HTTP 409 — ${JSON.stringify(detail)}`);
        }
      } else {
        const err = await r.json().catch(() => ({}));
        const detail = err?.detail || err?.message;
        setError(typeof detail === 'string' ? detail : `HTTP ${r.status}`);
      }
    } catch (e: any) {
      setError(e?.message || 'Network error');
    }
    setSubmitting(false);
  };

  return (
    <PageLayout
      title="CECL Admin"
      subtitle="Enable / disable the ASC 326-20 credit-loss engine for contract assets"
    >
      {error && (
        <div style={{
          padding: '10px 14px', marginBottom: 12, borderRadius: 6,
          background: 'rgba(239,68,68,0.10)', border: '1px solid rgba(239,68,68,0.30)',
          color: 'var(--accent-critical)', fontSize: 13,
        }}>
          {error}
        </div>
      )}

      {successMsg && (
        <div style={{
          padding: '10px 14px', marginBottom: 12, borderRadius: 6,
          background: 'rgba(34,197,94,0.10)', border: '1px solid rgba(34,197,94,0.30)',
          color: 'var(--positive)', fontSize: 13,
        }}>
          {successMsg}
        </div>
      )}

      {/* Current state */}
      <div style={{
        padding: 20, marginBottom: 20, borderRadius: 8,
        background: 'var(--bg-card)', border: '1px solid var(--border-subtle)',
      }}>
        <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-hint)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 6 }}>
          USE_CECL_FOR_CONTRACT_ASSETS
        </div>
        <div style={{
          fontSize: 28, fontWeight: 700,
          color: enabled === true ? 'var(--positive)'
            : enabled === false ? 'var(--warning)'
            : 'var(--text-hint)',
        }}>
          {loading ? 'Loading...'
            : enabled === true ? 'ENABLED'
            : enabled === false ? 'DISABLED'
            : 'UNKNOWN'}
        </div>
        <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 8, lineHeight: 1.5 }}>
          {enabled === true
            ? 'The CECL engine is live. New contract assets accrue provisions on control transfer; pool parameter changes trigger roll-forward entries to changes_to_preexisting.'
            : 'The CECL engine is inert. Pools may be configured and contracts assigned, but no allowances post and no JEs fire. Flip to ENABLED once pools are in place and all contracts with contract-asset exposure are assigned.'}
        </div>
      </div>

      {/* Toggle buttons */}
      <div style={{ display: 'flex', gap: 12 }}>
        {!enabled && (
          <ActionButton
            label={submitting ? 'Enabling...' : 'Enable CECL Engine'}
            variant="primary"
            onClick={() => flip(true)}
            disabled={submitting || loading}
          />
        )}
        {enabled && (
          <ActionButton
            label="Disable CECL Engine"
            variant="danger"
            onClick={() => setDisableConfirmOpen(true)}
            disabled={submitting || loading}
          />
        )}
      </div>

      {/* Supporting links */}
      <div style={{
        marginTop: 32, padding: 16, borderRadius: 8,
        background: 'var(--bg-card)', border: '1px solid var(--border-subtle)',
      }}>
        <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 12 }}>Related Pages</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <button
            onClick={() => navigate('/cecl-pools')}
            style={linkBtn}
          >
            CECL Pools — configure pools, methodology, Q-factor overlays
          </button>
          <button
            onClick={() => navigate('/cecl-disclosure')}
            style={linkBtn}
          >
            CECL Disclosure — ASC 326-20-50-13 roll-forward + backfill assignments
          </button>
        </div>
      </div>

      {/* CECL_BACKFILL_INCOMPLETE modal */}
      <Modal
        open={!!backfillBlock}
        onClose={() => setBackfillBlock(null)}
        size="lg"
        title="CECL_BACKFILL_INCOMPLETE"
        description="Enable blocked — unassigned contracts with contract-asset exposure exist. Assign a pool or an individual basis to each before re-enabling."
        footer={
          <>
            <ActionButton
              label="Close"
              variant="secondary"
              onClick={() => setBackfillBlock(null)}
            />
            <ActionButton
              label="Run Backfill First"
              variant="primary"
              onClick={() => { setBackfillBlock(null); navigate('/cecl-disclosure'); }}
            />
          </>
        }
      >
        <div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.5, marginBottom: 12 }}>
          {backfillBlock?.message}
        </div>

        {backfillBlock?.unassigned_count != null && (
          <div style={{
            padding: 12, borderRadius: 6,
            background: 'rgba(245,158,11,0.08)',
            border: '1px solid rgba(245,158,11,0.30)',
            marginBottom: 12,
          }}>
            <strong>{backfillBlock.unassigned_count}</strong> contract(s) unassigned.
          </div>
        )}

        {backfillBlock?.unassigned_contract_ids && backfillBlock.unassigned_contract_ids.length > 0 && (
          <div>
            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-hint)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 6 }}>
              Unassigned Contract IDs
            </div>
            <div style={{
              padding: 10, borderRadius: 4,
              background: 'var(--bg-deep)', border: '1px solid var(--border-subtle)',
              maxHeight: 200, overflowY: 'auto',
              display: 'flex', flexWrap: 'wrap', gap: 6,
            }}>
              {backfillBlock.unassigned_contract_ids.slice(0, 500).map(id => (
                <button
                  key={id}
                  onClick={() => navigate(`/contracts/${id}`)}
                  style={{
                    padding: '2px 8px', fontSize: 12, fontWeight: 600,
                    background: 'rgba(59,130,246,0.10)',
                    color: 'var(--accent)',
                    border: '1px solid rgba(59,130,246,0.30)',
                    borderRadius: 4, cursor: 'pointer',
                  }}
                >#{id}</button>
              ))}
              {backfillBlock.unassigned_contract_ids.length > 500 && (
                <span style={{ fontSize: 12, color: 'var(--text-hint)', padding: '2px 8px' }}>
                  ... and {backfillBlock.unassigned_contract_ids.length - 500} more
                </span>
              )}
            </div>
          </div>
        )}
      </Modal>

      {/* Disable confirmation modal */}
      <Modal
        open={disableConfirmOpen}
        onClose={() => setDisableConfirmOpen(false)}
        size="sm"
        title="Disable CECL Engine?"
        description="Turning off the engine stops ongoing allowance recompute."
        footer={
          <>
            <ActionButton
              label="Cancel"
              variant="secondary"
              onClick={() => setDisableConfirmOpen(false)}
              disabled={submitting}
            />
            <ActionButton
              label={submitting ? 'Disabling...' : 'Disable'}
              variant="danger"
              onClick={async () => { setDisableConfirmOpen(false); await flip(false); }}
              disabled={submitting}
            />
          </>
        }
      >
        <div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
          This stops ongoing allowance recompute. Existing allowances are preserved; no adjustments fire until re-enabled.
          External disclosures will continue to show the last-computed balance. Re-enable once operational issues are resolved.
        </div>
      </Modal>
    </PageLayout>
  );
}

const linkBtn: React.CSSProperties = {
  textAlign: 'left',
  padding: '10px 14px',
  border: '1px solid var(--border-subtle)',
  borderRadius: 6,
  background: 'var(--bg-card)',
  color: 'var(--accent)',
  fontSize: 13,
  fontWeight: 600,
  cursor: 'pointer',
  fontFamily: 'var(--font-sans)',
};
